import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DOCUMENT,
    ElementRef,
    Inject,
    Injector,
    Input,
    NgZone,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Overlay } from '@angular/cdk/overlay';
import { buildProviders, KalAutocompleteOption, KalInputComponent } from '@kalidea/kaligraphi';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap, take, tap } from 'rxjs/operators';
import gql from 'graphql-tag';
import {
    ChoixItemDto,
    MutationRechercherChoixParamReqSpecifArgs,
    ParamReqSpecifDto
} from 'src/graphql/generated-types';
import { CustomFormElement } from 'src/app/_core/tools/custom-form-element';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { arrayEmpty } from 'src/app/utils/array.utils';
import { LazyListPickerScrollStrategy } from 'src/app/_shared/components/lazy-list-picker/lazy-list-picker-scroll-strategy';

// Nombre minimal de caractères saisis avant de déclencher la recherche serveur (1 = dès la 1ʳᵉ lettre).
const MIN_KEYWORD_LENGTH = 1;
const MAX_RESULTS = 50;
const WARMUP_RESULTS = 1;

/**
 * Picker générique pour les paramètres « liste volumineuse » du requêteur (IsBigList) :
 * ne charge rien au démarrage, recherche les valeurs côté serveur à la frappe.
 */
@Component({
    selector: 'app-lazy-list-picker',
    templateUrl: './lazy-list-picker.component.html',
    styleUrls: ['./lazy-list-picker.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(LazyListPickerComponent),
    standalone: false
})
export class LazyListPickerComponent extends CustomFormElement<string> implements OnInit {

    @Input()
    parameter: ParamReqSpecifDto;

    @Input()
    label = '';

    control: FormControl<string | null>;

    optionsList: KalAutocompleteOption<ChoixItemDto>[] = [];

    /** Garde le panneau collé au champ au scroll (le conteneur du requêteur est invisible pour la stratégie par défaut). */
    readonly autocompleteScrollStrategy: LazyListPickerScrollStrategy;

    @ViewChild(KalInputComponent, {read: ElementRef})
    private kalInputRef: ElementRef<HTMLElement>;

    private _loading = false;

    constructor(private readonly injector: Injector,
                public readonly cdr: ChangeDetectorRef,
                private readonly graphqlService: GraphqlService,
                overlay: Overlay,
                ngZone: NgZone,
                @Inject(DOCUMENT) document: Document) {
        super();
        this.autocompleteScrollStrategy = new LazyListPickerScrollStrategy(
            overlay, ngZone, document, () => this.kalInputRef?.nativeElement);
    }

    get loading(): boolean {
        return this._loading;
    }

    set loading(loading: boolean) {
        this._loading = loading;
        this.cdr.markForCheck();
    }

    /**
     * Message affiché dans le panneau selon l'état :
     * - chargement en cours → aucun message (le loader suffit)
     * - champ ouvert sans saisie → invite à saisir (le panneau n'est plus vide « à l'aveugle »)
     * - recherche effectuée sans résultat → « Aucun résultat »
     */
    get emptyMessage(): string {
        if (this.loading) {
            return '';
        }
        if (!this.control?.value) {
            return 'Saisir pour rechercher';
        }
        return arrayEmpty(this.optionsList) ? 'Aucun résultat' : '';
    }

    writeValue(value: string): void {
        if (!value) {
            this.control?.patchValue(null, {emitEvent: false});
        }
    }

    ngOnInit(): void {
        this.control = this.createControlAndSubscriptions(this.injector);

        this.watch$ = this.control.valueChanges.pipe(
            tap(() => this.notifyUpdate(null)),
            debounceTime(300),
            tap(keyword => {
                if (!keyword || keyword.length < MIN_KEYWORD_LENGTH) {
                    this.optionsList = [];
                    this.loading = false;
                }
            }),
            filter(keyword => !!keyword && keyword.length >= MIN_KEYWORD_LENGTH),
            tap(() => this.loading = true),
            switchMap(keyword => this.searchChoices$(keyword).pipe(catchError(() => of([])))),
            tap(choices => {
                this.optionsList = choices.map(choice => ({label: choice.Libelle, value: choice}));
                this.loading = false;
            })
        );

        // Pré-chauffe : déclenche le chargement + cache serveur de la liste dès l'affichage du champ,
        // pendant que l'utilisateur lit le formulaire → la 1ère frappe est déjà « chaude ».
        // Souscription dédiée (hors valueChanges, non annulée par la frappe) et sans effet sur l'UI.
        this.watch$ = this.searchChoices$('', WARMUP_RESULTS).pipe(catchError(() => EMPTY));
    }

    pick(option: KalAutocompleteOption<ChoixItemDto>): void {
        if (!option) {
            return;
        }

        this.notifyUpdate(option.value.Id);
    }

    private searchChoices$(keyword: string, top: number = MAX_RESULTS): Observable<ChoixItemDto[]> {
        return this.graphqlService.mutateOne<'RechercherChoixParamReqSpecif', MutationRechercherChoixParamReqSpecifArgs>({
            mutation: gql`
                mutation RechercherChoixParamReqSpecif($contexte: ContextInfosInput, $idParam: Float, $motCle: String, $top: Int) {
                    RechercherChoixParamReqSpecif(contexte: $contexte, idParam: $idParam, motCle: $motCle, top: $top) {
                        ChoixItemDTO {
                            Id
                            Libelle
                        }
                    }
                }
            `,
            variables: {
                idParam: this.parameter?.Id,
                motCle: keyword,
                top
            }
        }).pipe(
            this.untilDestroyed,
            take(1),
            map(rechercherChoixParamReqSpecif => rechercherChoixParamReqSpecif?.ChoixItemDTO || [])
        );
    }
}
